import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {catchError, debounceTime, first} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import * as _ from 'lodash';
import {HttpErrorResponse} from '@angular/common/http';
import {LoginTranslationsService} from '~shared/translations';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {NotificationType} from '~core/enum';
import {CampaiMembership, Club, CombinedCustomer, Country, CustomerAttribute, Order, SidebarOptions} from '~core/models';
import {
  AttributeService,
  CustomerService,
  DefaultNotificationService,
  InvoiceService,
  MembershipService,
  MerchandisingService,
  NavigationService,
  StorageService
} from '~core/services';
import {MergeCustomerService} from "~features/customer-merger/merge-customer.service";

/**
 * Displays the customer merge process page.
 */
@UntilDestroy()
@Component({
  templateUrl: 'customer-merge-process.component.html',
})
export class CustomerMergeProcessComponent implements OnInit, OnDestroy {
  @ViewChild('topOfPage') topOfPage: ElementRef;
  public spinnerName: string = 'mergeProcessSpinner';
  public selectedClub: Club;
  public countries: Array<Country> = [];
  public firstCustomerData: any = [];
  public secondCustomerData: any = [];
  public combinedCustomerData: any = [];
  public finalCustomerData: CombinedCustomer;
  public stepProcess: number = 1;
  public isPotentialDuplicate: boolean = false;
  public loading: boolean = true;
  private sectionsNeeded: number = 2;
  private sectionsLoaded: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public i18nRequiredFieldsError = $localize`:@@notification.error.requiredFields:One or more required fields have not been selected`;
  public i18nRemovedDuplicateSuccess = $localize`:@@notification.success.removedDuplicate:These duplicates were successfully unlinked`;
  public i18nDuplicatesNotLinked = $localize`:@@notification.warning.duplicatesNotLinked:These duplicates were already unlinked`;
  public i18nRemovedDuplicateError = $localize`:@@notification.error.removedDuplicate:Could not unlink these duplicates`;
  public i18nCustomerMergeInitiated = $localize`:@@notification.success.customerMergeInitiated:The customer merge process has been successfully initiated!`;
  public i18nCustomerMergeFailed = $localize`:@@notification.success.customerMergeFailed:Could not initate customer merge!`;
  public i18nCustomerMergeOngoing = $localize`:@@notification.warning.customerMergeOngoing:The initiated customer merge process was not yet fully processed and might be still running.`;

  constructor(
    public navigationService: NavigationService,
    private route: ActivatedRoute,
    private router: Router,
    private storage: StorageService,
    private customerService: CustomerService,
    private sidebarService: SidebarService,
    private location: Location,
    public spinnerService: SpinnerService,
    public attributeService: AttributeService,
    public buttonsTranslations: ButtonsTranslations,
    public notificationService: DefaultNotificationService,
    public membershipService: MembershipService,
    private invoiceService: InvoiceService,
    private ordersService: MerchandisingService,
    private customerMergeService: MergeCustomerService,
    private loginTranslationsService: LoginTranslationsService
  ) {
    this.sidebarService.setSidebar(new SidebarOptions(false, false));
    this.navigationService.setCurrentPage('customerMerger');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'customerMerger']);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
    this.storage.castCurrentCountries.pipe(untilDestroyed(this))
      .subscribe(countries => this.countries = countries);
    this.customerMergeService.castCombinedCustomer.pipe(untilDestroyed(this))
      .subscribe(data => this.combinedCustomerData = data);
    this.customerMergeService.castFirstCustomerData.pipe(untilDestroyed(this))
      .subscribe(data => this.firstCustomerData = data);
    this.customerMergeService.castSecondCustomerData.pipe(untilDestroyed(this))
      .subscribe(data => this.secondCustomerData = data);
  }

  public ngOnInit(): void {
    if (!this.selectedClub.tixxCustomerMergeAllowed) {
      this.notificationService.showNotification({
        type: NotificationType.WARNING,
        message: this.loginTranslationsService.i18nErrorNoAccess,
      });
      this.router.navigate([`/club/${this.selectedClub.id}`]);
    }

    this.sectionsLoaded.pipe(untilDestroyed(this))
      .subscribe(num => this.sectionsNeeded === num ? this.loading = false : null);

    if (this.selectedClub.modules !== null) {
      this.loadBothCustomer();
    }
  }

  public ngOnDestroy(): void {
  }

  public goBack() {
    if (this.stepProcess === 1) {
      this.location.back();
    } else {
      this.stepProcess--;
      this.scrollToTop();
    }
  }

  public goNext() {
    if (this.stepProcess === 1) {
      this.stepProcess++;
      this.scrollToTop();
    } else if (this.stepProcess === 2) {
      if (this.customerMergeService.isAssembledCustomerValid()) {
        this.stepProcess++;
        this.scrollToTop();
      } else {
        this.customerMergeService.setTriggered(true);
        this.notificationService.showNotification({
          type: NotificationType.ALERT,
          message: this.i18nRequiredFieldsError
        });
        this.scrollToTop();
      }
    } else if (this.stepProcess === 3) {
      this.spinnerService.show(this.spinnerName);
      const sourceCustomer = this.combinedCustomerData.id === this.firstCustomerData[0].id ? this.secondCustomerData[0] : this.firstCustomerData[0];
      const targetCustomer = this.combinedCustomerData.id === this.secondCustomerData[0].id ? this.secondCustomerData[0] : this.firstCustomerData[0];
      this.finalCustomerData = new CombinedCustomer(this.combinedCustomerData, sourceCustomer);

      if (this.selectedClub.isCampaiMembershipActive) {
        delete this.finalCustomerData.membership;
      } else if (this.selectedClub.isCrmMembershipActive) {
        delete this.finalCustomerData.campaiMembership;
      }

      this.customerService.mergeCustomer(this.finalCustomerData).pipe(untilDestroyed(this))
        .subscribe({
          next: () => {
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.i18nCustomerMergeInitiated
            });
            setTimeout(() => {
              this.customerService.advancedSearchBy(`externalNumber=${sourceCustomer.externalNumber}`, this.selectedClub.id).pipe(debounceTime(3000))
                .subscribe({
                  next: res => {
                    if (res.length) {
                      this.notificationService.showNotification({
                        type: NotificationType.WARNING,
                        message: this.i18nCustomerMergeOngoing
                      });
                    }
                    this.router.navigate([`club/${this.selectedClub.id}/customer/${targetCustomer.id}`]);
                  }
                });
            }, 3000);
          },
          error: (error: HttpErrorResponse) => {
            this.spinnerService.hide(this.spinnerName);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.i18nCustomerMergeFailed
            });
          }
        });
    }
  }

  loadBothCustomer() {
    let firstCustomerId;
    let secondCustomerId;
    let firstCustomer = [];
    let secondCustomer = [];

    if (window.history.state.fromLocation) {
      this.isPotentialDuplicate = true;
    }

    this.route.queryParams
      .subscribe(params => {
        firstCustomerId = params?.firstCustomer;
        secondCustomerId = params?.secondCustomer;
      });
    const calls1: Array<undefined | Observable<any>> = [
      this.customerService.getById(firstCustomerId).pipe(first(), catchError(error => of(null))),
      this.attributeService.getCustomerAttribute(firstCustomerId).pipe(first(), catchError(error => of(null))),
      this.invoiceService.getCustomerInvoices(firstCustomerId, 3).pipe(first(), catchError(error => of(null))),
    ];
    const calls2: Array<undefined | Observable<any>> = [
      this.customerService.getById(secondCustomerId).pipe(first(), catchError(error => of(null))),
      this.attributeService.getCustomerAttribute(secondCustomerId).pipe(first(), catchError(error => of(null))),
      this.invoiceService.getCustomerInvoices(secondCustomerId, 3).pipe(first(), catchError(error => of(null))),
    ];
    const optionalCalls1: Array<undefined | Observable<any>> = [];
    const optionalCalls2: Array<undefined | Observable<any>> = [];

    if (this.selectedClub.isCampaiMembershipActive) {
      this.sectionsNeeded = 4;
      optionalCalls1.push(
        this.membershipService.getCampaiMembership(firstCustomerId).pipe(first(), catchError(error => of(null))),
      );
      optionalCalls2.push(
        this.membershipService.getCampaiMembership(secondCustomerId).pipe(first(), catchError(error => of(null)))
      );
    }

    if (this.selectedClub.isMerchandisingActive) {
      this.sectionsNeeded = 4;
      optionalCalls1.push(
        this.ordersService.getAll(firstCustomerId, 1, 3).pipe(first(), catchError(error => of(null))),
      );
      optionalCalls2.push(
        this.ordersService.getAll(secondCustomerId, 1, 3).pipe(first(), catchError(error => of(null))),
      );
    }

    forkJoin(calls1).subscribe((
      [fC, fCAttributes, fCInvoices]
    ) => {
      firstCustomer = [fC];
      firstCustomer[0]['attributes'] = this.getAttributes(fCAttributes as Array<CustomerAttribute>);
      firstCustomer[0]['invoices'] = fCInvoices;
      this.sectionsLoaded.next(this.sectionsLoaded.getValue() + 1);
      this.customerMergeService.setFirstCustomer(firstCustomer);

      forkJoin(optionalCalls1).subscribe((results: Array<any>) => {

        for (const data of results) {
          if (data && data[0] instanceof Order) {
            firstCustomer[0]['merchandising'] = data;
          }
          if (data instanceof CampaiMembership) {
            firstCustomer[0]['campaiMembership'] = data;
          }
        }
        this.sectionsLoaded.next(this.sectionsLoaded.getValue() + 1);
        this.customerMergeService.setFirstCustomer(firstCustomer);
      });
    });

    forkJoin(calls2).subscribe((
      [sC, sCAttributes, sCInvoices]
    ) => {
      secondCustomer = [sC];
      secondCustomer[0]['attributes'] = this.getAttributes(sCAttributes as Array<CustomerAttribute>);
      secondCustomer[0]['invoices'] = sCInvoices;
      this.sectionsLoaded.next(this.sectionsLoaded.getValue() + 1);
      this.customerMergeService.setSecondCustomer(secondCustomer);

      forkJoin(optionalCalls2).subscribe((results: Array<any>) => {
        for (const data of results) {
          if (data && data[0] instanceof Order) {
            secondCustomer[0]['merchandising'] = data;
          }
          if (data instanceof CampaiMembership) {
            secondCustomer[0]['campaiMembership'] = data;
          }
        }
        this.sectionsLoaded.next(this.sectionsLoaded.getValue() + 1);
        this.customerMergeService.setSecondCustomer(secondCustomer);
      });
    });
  }

  /**
   * Function that fetches all the attributes
   */
  public getAttributes(customerData: Array<CustomerAttribute>): Array<CustomerAttribute> {
    const arr = [];
    for (const el of customerData) {
      if (!el.attribute.internal) {
        el.attribute['customerId'] = el.id;
        arr.push(el.attribute);
      }
    }
    return arr;
  }

  public removePotentialDuplicates(): void {
    const data = {
      first: `/api/customers/${this.firstCustomerData[0].id}`,
      second: `/api/customers/${this.secondCustomerData[0].id}`
    };

    this.customerService.removeDuplicates(data).pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: this.i18nRemovedDuplicateSuccess
          });
          this.location.back();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.notificationService.showNotification({
              type: NotificationType.WARNING,
              message: this.i18nDuplicatesNotLinked
            });
            this.location.back();
          } else {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.i18nRemovedDuplicateError
            });
          }
        }
      });
  }

  /**
   * Scroll function
   */
  public scrollToTop(): void {
    const scrollTimeout = 100;
    setTimeout(() => {
      this.topOfPage.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, scrollTimeout);
  }

}
